import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const LOSS_REASONS = ['EXPIRED', 'DAMAGED', 'LOST', 'THEFT', 'OTHER'] as const;

type ProductOption = { id: string; name: string; sku: string };
type LotOption = { id: string; batchCode: string | null; remainingQtyLbs: number; expiryDate: string | null };
type LossRow = {
  id: string;
  productId: string;
  lotId: string | null;
  quantityLbs: number;
  reason: string;
  notes: string | null;
  unitCostAtLoss: number | null;
  createdAt: string;
  product: {
    id: string;
    name: string;
    sku: string;
    department?: { id: string; name: string; slug: string } | null;
  };
  lot: { id: string; batchCode: string | null } | null;
};

function LossesPage() {
  const { authorizedFetch } = useAuth();
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const [losses, setLosses] = useState<LossRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [productId, setProductId] = useState('');
  const [lotId, setLotId] = useState('');
  const [quantityLbs, setQuantityLbs] = useState('');
  const [reason, setReason] = useState<'EXPIRED' | 'DAMAGED' | 'LOST' | 'THEFT' | 'OTHER'>('EXPIRED');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [lots, setLots] = useState<LotOption[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [filterReason, setFilterReason] = useState<string>('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);

  const isAllScope = React.useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const scopedDepartmentSlug = React.useMemo(() => {
    if (isAllScope) return '';
    return String(workspaceScope || '').trim().toLowerCase();
  }, [isAllScope, workspaceScope]);
  const switchWorkspaceScope = React.useCallback((nextDepartmentSlug: string) => {
    const slug = String(nextDepartmentSlug || '').trim().toLowerCase();
    if (!slug || typeof window === 'undefined') return;
    window.localStorage.setItem('workspace_department_scope', slug);
    window.dispatchEvent(
      new CustomEvent('workspace-department-scope:change', {
        detail: { scope: slug },
      }),
    );
  }, []);

  const loadLosses = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '20');
      if (filterReason) params.set('reason', filterReason);
      if (filterFrom) params.set('from', filterFrom);
      if (filterTo) params.set('to', filterTo);
      if (scopedDepartmentSlug) params.set('departmentSlug', scopedDepartmentSlug);
      const res = await authorizedFetch(`${API_BASE}/inventory/losses?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load losses');
      const data = await res.json();
      setLosses(data.items);
      setTotal(data.total);
      setTotalValue(data.totalValue ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, page, filterReason, filterFrom, filterTo, scopedDepartmentSlug]);

  useEffect(() => {
    void loadLosses();
  }, [loadLosses]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const clearFilters = () => {
    setFilterReason('');
    setFilterFrom('');
    setFilterTo('');
    setPage(1);
  };

  const onFilterChange = () => setPage(1);

  useEffect(() => {
    if (!productSearch) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        const res = await authorizedFetch(
          (() => {
            const url = new URL(`${API_BASE}/catalog/products`);
            url.searchParams.set('q', productSearch);
            url.searchParams.set('pageSize', '20');
            url.searchParams.set('activeOnly', 'true');
            if (scopedDepartmentSlug) url.searchParams.set('departmentSlug', scopedDepartmentSlug);
            return url.toString();
          })(),
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setProducts(data.items.map((p: { id: string; name: string; sku: string }) => ({ id: p.id, name: p.name, sku: p.sku })));
      } catch {
        if (!cancelled) setProducts([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [productSearch, authorizedFetch, scopedDepartmentSlug]);

  useEffect(() => {
    if (!productId) {
      setLots([]);
      setLotId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${API_BASE}/inventory/lots`);
        url.searchParams.set('productId', productId);
        url.searchParams.set('pageSize', '100');
        url.searchParams.set('onlyWithStock', 'true');
        if (scopedDepartmentSlug) url.searchParams.set('departmentSlug', scopedDepartmentSlug);
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setLots(data.items.map((l: { id: string; batchCode: string | null; remainingQtyLbs: number; expiryDate: string | null }) => ({ id: l.id, batchCode: l.batchCode, remainingQtyLbs: l.remainingQtyLbs, expiryDate: l.expiryDate })));
        if (!cancelled && data.items.length > 0 && !lotId) setLotId(data.items[0].id);
      } catch {
        if (!cancelled) setLots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, authorizedFetch, lotId, scopedDepartmentSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const qty = Number(quantityLbs);
    if (Number.isNaN(qty) || qty <= 0) {
      setError('Invalid quantity');
      return;
    }
    if (!productId) {
      setError('Select a product');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/inventory/losses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          lotId: lotId || undefined,
          quantityLbs: qty,
          reason,
          notes: notes || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Failed to record loss');
      }
      setShowForm(false);
      setProductId('');
      setLotId('');
      setQuantityLbs('');
      setNotes('');
      setProductSearch('');
      loadLosses();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.losses_title', 'Record losses')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.losses_subtitle', 'Record product losses (expired, damaged, lost). Deducts from stock and from active budget.')}
          </p>
        </section>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 sm:p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('backoffice.losses_total_value', 'Total loss value')}
            {(filterReason || filterFrom || filterTo) && (
              <span className="ml-2 font-normal normal-case">({t('backoffice.losses_filtered', 'filtered')})</span>
            )}
          </h2>
          <p className="mt-1 text-2xl font-bold text-red-600 sm:text-3xl">
            {formatPrice(totalValue)}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900">{t('backoffice.losses_history', 'Loss history')}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filterReason}
                onChange={(e) => { setFilterReason(e.target.value); onFilterChange(); }}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                title={t('backoffice.losses_filter_by_reason', 'Filter by reason')}
              >
                <option value="">{t('backoffice.losses_all_reasons', 'All reasons')}</option>
                {LOSS_REASONS.map((r) => (
                  <option key={r} value={r}>{t(`admin.loss_reason_${r.toLowerCase()}`, r)}</option>
                ))}
              </select>
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => { setFilterFrom(e.target.value); onFilterChange(); }}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                title={t('backoffice.losses_from_date', 'From date')}
              />
              <input
                type="date"
                value={filterTo}
                onChange={(e) => { setFilterTo(e.target.value); onFilterChange(); }}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                title={t('backoffice.losses_to_date', 'To date')}
              />
              {(filterReason || filterFrom || filterTo) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100"
                >
                  {t('backoffice.losses_clear_filters', 'Clear filters')}
                </button>
              )}
            </div>
          </div>
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => setShowForm((x) => !x)}
              disabled={isAllScope}
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {showForm ? t('common.cancel') : t('backoffice.losses_record', 'Record loss')}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-6 grid gap-3 border-b border-slate-100 pb-6 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-slate-700">{t('backoffice.losses_product', 'Product')}</label>
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={t('backoffice.losses_search_product', 'Search product')}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                {products.length > 0 && (
                  <div className="mt-1 max-h-32 overflow-y-auto rounded border border-slate-200 bg-white">
                    {products.map((pr) => (
                      <button
                        key={pr.id}
                        type="button"
                        onClick={() => {
                          setProductId(pr.id);
                          setProductSearch(pr.name);
                          setProducts([]);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        {pr.name} ({pr.sku})
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {productId && lots.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-700">{t('backoffice.losses_lot', 'Lot (optional)')}</label>
                  <select
                    value={lotId}
                    onChange={(e) => setLotId(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— No specific lot</option>
                    {lots.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.batchCode || l.id.slice(-6)} — {l.remainingQtyLbs} lb
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-700">{t('backoffice.losses_quantity', 'Quantity (lb)')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={quantityLbs}
                  onChange={(e) => setQuantityLbs(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700">{t('backoffice.losses_reason', 'Reason')}</label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as typeof reason)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {LOSS_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`admin.loss_reason_${r.toLowerCase()}`, r)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-700">{t('backoffice.losses_notes', 'Notes')}</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <button type="submit" disabled={saving} className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saving ? t('common.saving') : t('backoffice.losses_submit', 'Record loss')}
              </button>
            </form>
          )}

          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.budget_loading', 'Loading…')}
              skeletonRows={4}
            />
          ) : losses.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.losses_empty', 'No losses recorded yet.')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="border-b border-slate-200 text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">{t('backoffice.losses_col_date', 'Date')}</th>
                      <th className="py-2 pr-4">{t('backoffice.losses_col_product', 'Product')}</th>
                      <th className="py-2 pr-4">{t('backoffice.losses_col_lot', 'Lot')}</th>
                      {isAllScope && <th className="py-2 pr-4">{t('backoffice.losses_col_department', 'Department')}</th>}
                      <th className="py-2 pr-4">{t('backoffice.losses_col_qty', 'Qty (lb)')}</th>
                      <th className="py-2 pr-4 text-right">{t('backoffice.losses_col_value', 'Value')}</th>
                      <th className="py-2 pr-4">{t('backoffice.losses_col_reason', 'Reason')}</th>
                      <th className="py-2 pr-4">{t('backoffice.losses_col_notes', 'Notes')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {losses.map((l) => {
                      const value = l.quantityLbs * (l.unitCostAtLoss ?? 0);
                      return (
                        <tr key={l.id}>
                          <td className="py-2 pr-4 text-slate-700">{new Date(l.createdAt).toLocaleString()}</td>
                          <td className="py-2 pr-4 text-slate-800">{l.product.name} ({l.product.sku})</td>
                          <td className="py-2 pr-4 text-slate-700">{l.lot?.batchCode ?? '—'}</td>
                          {isAllScope && (
                            <td className="py-2 pr-4 text-slate-700">
                              {l.product.department?.slug ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    switchWorkspaceScope(l.product.department?.slug || '')
                                  }
                                  className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
                                  title={t(
                                    'backoffice.losses_switch_department',
                                    'Switch scope to this department',
                                  )}
                                >
                                  {l.product.department?.name || '—'}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          <td className="py-2 pr-4 text-slate-700">{l.quantityLbs.toFixed(2)}</td>
                          <td className="py-2 pr-4 text-right font-medium text-red-700">{formatPrice(value)}</td>
                          <td className="py-2 pr-4 text-slate-700">{t(`admin.loss_reason_${l.reason.toLowerCase()}`, l.reason)}</td>
                          <td className="py-2 pr-4 text-slate-600">{l.notes ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-4 flex justify-end gap-2 text-xs text-slate-600">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('backoffice.inventory_prev_page', 'Prev')}
                  </button>
                  <span>{page} / {totalPages}</span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('backoffice.inventory_next_page', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default LossesPage;

